// Direct imports
import { Scene } from "@babylonjs/core/scene";
import { ArcRotateCamera } from "@babylonjs/core/Cameras/arcRotateCamera";
import type { Camera } from "@babylonjs/core/Cameras/camera";
import { ArcRotateCameraMovement } from "@babylonjs/core/Cameras/arcRotateCameraMovement";
// Babylon's animation side effects. Since 9.15 (BabylonJS#18620) nothing in
// Babylon imports them any more: without them Scene.beginAnimation is a
// silent stub, so FramingBehavior's animated framing (F to focus, animated
// reset) never moves the camera, and Animation.CreateAndStartAnimation from
// a host does nothing either. Scene is shared, so this covers every caller.
import "@babylonjs/core/Animations/animation";
import "@babylonjs/core/Animations/animatable";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { FramingBehavior } from "@babylonjs/core/Behaviors/Cameras/framingBehavior";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";
import { CreateSphere } from "@babylonjs/core/Meshes/Builders/sphereBuilder";
import type { IParticleEmitterType } from "@babylonjs/core/Particles/EmitterTypes/IParticleEmitterType";
import { ConfigData } from './configdata';
import { Logger } from '../utils/Logger';
import { ANCHOR_PREVIEW_MESH_NAME } from '../scenedata/behaviors/imageanchordata';
import type { SafeAreaInsets } from '../scenedata/common';
import { COLLIDER_MESH_SUFFIX, SELECTION_MESH_SUFFIX } from '../behaviors/boxcollider';

/**
 * Interface for particle emitter types that have radius properties.
 * This covers SphereParticleEmitter, ConeParticleEmitter, CylinderParticleEmitter, etc.
 */
interface IRadialParticleEmitter extends IParticleEmitterType {
    radius: number;
    radiusRange: number;
}

// 2025
// Author: Aaron Post

export const RENDER_BOUNDS_NAME = "Render Bounds";

/**
 * Floor of the near clip plane, in metres (1 cm). The plane rises with the
 * orbit distance — see {@link SceneCamera.applyClipPlanes}.
 */
export const CAMERA_MIN_Z = 0.01;

/**
 * Fixed far clip plane, in metres (1 km) — see {@link SceneCamera.applyClipPlanes}.
 */
export const CAMERA_MAX_Z = 1000;

/**
 * Largest depth-buffer step, in metres, allowed at the orbit target.
 *
 * Generated scenes lift text 1.5 mm off the panel behind it. When the depth
 * step is as coarse as that lift, text and panel quantise to the same depth
 * and the glyphs are dropped or striped. Half the lift keeps them apart.
 */
const CAMERA_MAX_DEPTH_STEP = 0.00075;

/** Ceiling for the distance-following near plane, in metres. */
const CAMERA_MAX_NEAR_Z = 0.5;

/** Floor each camera's near plane drops back to when it is not zoomed out. */
const nearFloors = new WeakMap<ArcRotateCamera, number>();

/**
 * Options for focusing the camera on a single node ({@link SceneCamera.focusOnNode}).
 */
export interface CameraFocusOptions {
    /**
     * Padding multiplier around the object bounds (1 = exact fit).
     * @default 1.5
     */
    radiusScale?: number;
    /**
     * Framing animation duration in milliseconds (0 = instant).
     * @default 300
     */
    framingTime?: number;
}

interface FramingOverrides {
    framingTime?: number;
    radiusScale?: number;
}

/**
 * Pending restores for temporary framing overrides, keyed per behavior so
 * overlapping calls (e.g. rapid focus taps) never restore stale temp values.
 */
const pendingFramingRestores = new WeakMap<FramingBehavior, {
    timer: ReturnType<typeof setTimeout>;
    saved: FramingOverrides;
}>();

/**
 * Apply temporary overrides to a FramingBehavior and restore the originals
 * after `restoreAfterMs`. If a restore is already pending, it is settled
 * first so the saved values are always the true originals — naive
 * save/restore pairs stack wrong under rapid calls and leak the temp values.
 */
function applyTemporaryFraming(
    framing: FramingBehavior,
    overrides: FramingOverrides,
    restoreAfterMs: number
): void {
    const pending = pendingFramingRestores.get(framing);
    if (pending) {
        clearTimeout(pending.timer);
        Object.assign(framing, pending.saved);
        pendingFramingRestores.delete(framing);
    }

    const saved: FramingOverrides = {};
    if (overrides.framingTime !== undefined) {
        saved.framingTime = framing.framingTime;
        framing.framingTime = overrides.framingTime;
    }
    if (overrides.radiusScale !== undefined) {
        saved.radiusScale = framing.radiusScale;
        framing.radiusScale = overrides.radiusScale;
    }

    const timer = setTimeout(() => {
        Object.assign(framing, saved);
        pendingFramingRestores.delete(framing);
    }, restoreAfterMs);
    pendingFramingRestores.set(framing, { timer, saved });
}

/**
 * The shift, in normalized device coordinates, that framing gave each camera's
 * picture: a lens shift that keeps the orbit pivot on the content and holds
 * at any zoom. Applied on every projection rebuild (resize, fov), so it lasts
 * until the next framing sets it again.
 */
const lensShifts = new WeakMap<Camera, { x: number; y: number }>();

/** Post-multiply the camera's fresh projection by the shift: clip x += shift.x * w, y likewise. */
function shiftProjection(camera: Camera): void {
    const shift = lensShifts.get(camera);
    if (!shift || (shift.x === 0 && shift.y === 0)) return;
    // The observer runs right after the rebuild, so this returns that matrix without recomputing.
    const matrix = camera.getProjectionMatrix();
    for (let row = 0; row < 4; row++) {
        const w = matrix.m[row * 4 + 3];
        if (w === 0) continue;
        matrix.addAtIndex(row * 4, shift.x * w);
        matrix.addAtIndex(row * 4 + 1, shift.y * w);
    }
}

function setLensShift(camera: Camera, x: number, y: number): void {
    const known = lensShifts.has(camera);
    if (!known && x === 0 && y === 0) return;
    lensShifts.set(camera, { x, y });
    if (!known) camera.onProjectionMatrixChangedObservable.add(() => shiftProjection(camera));
    // Rebuild now, so picking and the next frame see the new shift.
    camera.getProjectionMatrix(true);
}

/**
 * Visible viewport fractions and the lens shift that centres the picture there.
 * Insets that leave nothing uncovered are ignored.
 */
function framingWindow(camera: Camera, insets?: Readonly<SafeAreaInsets>): { width: number; height: number; x: number; y: number } {
    const none = { width: 1, height: 1, x: 0, y: 0 };
    if (!insets) return none;
    const engine = camera.getScene().getEngine();
    const rect = engine.getRenderingCanvasClientRect();
    const width = rect?.width || engine.getRenderWidth();
    const height = rect?.height || engine.getRenderHeight();
    const left = Math.max(0, insets.left);
    const right = Math.max(0, insets.right);
    const top = Math.max(0, insets.top);
    const bottom = Math.max(0, insets.bottom);
    const visibleWidth = width - left - right;
    const visibleHeight = height - top - bottom;
    if (!width || !height || visibleWidth <= 0 || visibleHeight <= 0) return none;
    return {
        width: visibleWidth / width,
        height: visibleHeight / height,
        x: (left - right) / width,
        y: (bottom - top) / height,
    };
}

/**
 * Distance that fits the world box in the current view, with padding in the image plane.
 * Null when the view has no usable aspect (a canvas with no size, such as a hidden pane
 * before layout), so the caller keeps the sphere fit instead of a NaN radius.
 */
function boxFramingRadius(camera: ArcRotateCamera, min: Vector3, max: Vector3, framing: Pick<FramingBehavior, 'positionScale' | 'radiusScale'>, window: ReturnType<typeof framingWindow>): number | null {
    const aspect = camera.getEngine().getAspectRatio(camera);
    const slope = Math.tan(camera.fov / 2);
    const slopeX = (camera.fovMode === ArcRotateCamera.FOVMODE_HORIZONTAL_FIXED ? slope : slope * aspect) * window.width;
    const slopeY = (camera.fovMode === ArcRotateCamera.FOVMODE_HORIZONTAL_FIXED ? slope / aspect : slope) * window.height;
    const target = min.add(max).scaleInPlace(0.5);
    target.y = min.y + (max.y - min.y) * framing.positionScale;
    const rotation = camera.getViewMatrix();
    const handedness = camera.getScene().useRightHandedSystem ? 1 : -1;
    // The near plane the camera will have on arrival. A close fit gets the floor;
    // `camera.minZ` still follows the zoom the camera is leaving. A camera this file
    // does not manage keeps the plane it has.
    const near = nearFloors.get(camera) ?? camera.minZ;
    let radius = near;
    for (const x of [min.x, max.x]) for (const y of [min.y, max.y]) for (const z of [min.z, max.z]) {
        const corner = Vector3.TransformNormal(new Vector3(x, y, z).subtractInPlace(target), rotation);
        const towardCamera = corner.z * handedness;
        radius = Math.max(radius, towardCamera + near,
            towardCamera + framing.radiusScale * Math.max(Math.abs(corner.x) / slopeX, Math.abs(corner.y) / slopeY));
    }
    // Projection matrices are float32: leave one unit of roundoff at an exactly fitted edge.
    const fitted = radius * (1 + 2 ** -23);
    return Number.isFinite(fitted) ? fitted : null;
}

/** Keep Babylon's framing animation and limits, but fit the box rather than its enclosing sphere. */
function zoomInto(camera: ArcRotateCamera, framing: FramingBehavior, min: Vector3, max: Vector3, safeArea?: Readonly<SafeAreaInsets>): void {
    const view = framingWindow(camera, safeArea);
    setLensShift(camera, view.x, view.y);
    const scale = framing.radiusScale;
    // Babylon multiplies this unit-padding sphere distance by radiusScale.
    const sphereRadius = camera._calculateLowerRadiusFromModelBoundingSphere(min, max);
    const fitted = sphereRadius > 0 ? boxFramingRadius(camera, min, max, framing, view) : null;
    if (fitted !== null) framing.radiusScale = fitted / sphereRadius;
    try { framing.zoomOnBoundingInfo(min, max); }
    finally { framing.radiusScale = scale; }
}

/**
 * The ArcRotateCamera movement system, integrated once per frame.
 *
 * `ArcRotateCamera._checkInputs` integrates `camera.movement` (turns the frame's
 * pointer and wheel input into velocities and deltas), applies the deltas, then
 * calls `super._checkInputs()`. Since Babylon 9.12.1 (BabylonJS#18573) that is
 * `TargetCamera._checkInputs`, which folds FreeCamera input into the same
 * movement and integrates it a second time, so every velocity decays twice a
 * frame. It then applies TargetCamera's glide cutoff, `speed * _panningEpsilon`
 * against the frame's pan delta, which zeroes the pan velocity whenever a frame
 * pans less than 2 mm: on an asset-sized scene that is most frames, and a
 * right-drag pan moves a small fraction of the distance it did on 9.11.
 *
 * Here a second integration in the same frame is a no-op, and the pan reset
 * that follows it is ignored, so the camera integrates once per frame as
 * ArcRotateCamera is written to, and the pan glide ends at the movement's own
 * velocity epsilon.
 */
class SingleIntegrationArcRotateCameraMovement extends ArcRotateCameraMovement {
    /** Frame whose input was integrated. */
    private _integratedFrameId = -1;
    /** Frame in which TargetCamera._checkInputs re-entered after the integration. */
    private _reenteredFrameId = -1;

    public override computeCurrentFrameDeltas(): void {
        const frameId = this._scene.getFrameId();
        if (frameId === this._integratedFrameId) {
            this._reenteredFrameId = frameId;
            return;
        }
        this._integratedFrameId = frameId;
        super.computeCurrentFrameDeltas();
    }

    public override resetPanVelocity(): void {
        // Swallow only the one reset TargetCamera's re-entered pass makes after
        // this frame's integration; any other reset goes through.
        if (this._reenteredFrameId === this._scene.getFrameId()) {
            this._reenteredFrameId = -1;
            return;
        }
        super.resetPanVelocity();
    }
}

export class SceneCamera {
    public static setupCamera(scene: Scene): ArcRotateCamera {
        scene.useRightHandedSystem = true;

        const camera = new ArcRotateCamera(
            "Camera2",
            Math.PI / 2,
            Math.PI / 2,
            3,
            new Vector3(0, 0, 0),
            scene
        );
        // Swapped in before the inertia below is set: the camera's inertia
        // setters write through to `movement`. The new movement starts with
        // Babylon's default pan inertia, so copy the camera's panningInertia.
        camera.movement = new SingleIntegrationArcRotateCameraMovement(scene, camera.position);
        camera.movement.panInertia = camera.panningInertia;

        // Framing a translated target must retain the view axes used to fit its box.
        camera.overrideCloneAlphaBetaRadius = true;

        // Zoom distance
        camera.lowerRadiusLimit = 0.01;
        camera.upperRadiusLimit = 50;

        // Clip planes start fixed; the near plane then follows the orbit distance
        // (syncNearPlane) — see applyClipPlanes().
        camera.minZ = CAMERA_MIN_Z;
        camera.maxZ = CAMERA_MAX_Z;
        nearFloors.set(camera, CAMERA_MIN_Z);
        // Zooming out coarsens the depth buffer; keep the near plane in step.
        camera.onViewMatrixChangedObservable.add(() => SceneCamera.syncNearPlane(camera));

        // Camera feel
        camera.wheelPrecision = 0.25;
        camera.wheelDeltaPercentage = 0.015;
        camera.inertia = 0.9;

        // Panning is available via:
        // - Ctrl + Left mouse button + drag
        // - Right mouse button + drag
        // - Middle mouse button + drag (scroll wheel click + drag)

        // Enable auto-framing behavior
        camera.useFramingBehavior = true;

        const framingBehavior = camera.getBehaviorByName("Framing") as FramingBehavior;
        if (framingBehavior) {
            framingBehavior.framingTime = 0; // Instant framing, no animation
            framingBehavior.mode = FramingBehavior.FitFrustumSidesMode; // Fit the entire object in view
            framingBehavior.elevationReturnTime = -1; // Disable auto-correction of camera pitch
        }

        return camera;
    }


    private static estimateParticleSystemBounds(scene: Scene): { min: Vector3, max: Vector3 } | null {
        if (!scene.particleSystems || scene.particleSystems.length === 0) {
            return null;
        }

        const min = new Vector3(Number.MAX_VALUE, Number.MAX_VALUE, Number.MAX_VALUE);
        const max = new Vector3(-Number.MAX_VALUE, -Number.MAX_VALUE, -Number.MAX_VALUE);
        let foundAny = false;

        for (const ps of scene.particleSystems) {
            // Get emitter position (could be a mesh or transform node)
            let emitterPos = Vector3.Zero();
            if (ps.emitter) {
                if (ps.emitter instanceof AbstractMesh) {
                    emitterPos = ps.emitter.getAbsolutePosition();
                } else if (ps.emitter instanceof TransformNode) {
                    emitterPos = ps.emitter.position;
                }
            }

            // Estimate bounds based on emitter type and particle system properties
            let estimatedRadius = 1; // Default radius

            // Try to get emitter radius if available (for radial emitters like sphere, cone, cylinder)
            if (ps.particleEmitterType) {
                const emitterType = ps.particleEmitterType as Partial<IRadialParticleEmitter>;
                if (emitterType.radius !== undefined) {
                    estimatedRadius = Math.max(estimatedRadius, emitterType.radius);
                }
                if (emitterType.radiusRange !== undefined) {
                    estimatedRadius = Math.max(estimatedRadius, emitterType.radiusRange);
                }
            }

            // Account for particle velocity and lifetime for a rough bounds estimate
            // This is mostly empirical, couldn't get reasonable physical parameters to work consistently.
            const velocityEstimate = Math.max(
                Math.abs(ps.minEmitPower || 0),
                Math.abs(ps.maxEmitPower || 0)
            );

            // Rough estimate of how far particles can travel
            const maxTravelDistance = velocityEstimate * .75;
            const totalRadius = estimatedRadius + maxTravelDistance;

            // Expand bounds
            min.x = Math.min(min.x, emitterPos.x - totalRadius);
            min.y = Math.min(min.y, emitterPos.y - totalRadius);
            min.z = Math.min(min.z, emitterPos.z - totalRadius);

            max.x = Math.max(max.x, emitterPos.x + totalRadius);
            max.y = Math.max(max.y, emitterPos.y + totalRadius);
            max.z = Math.max(max.z, emitterPos.z + totalRadius);

            foundAny = true;
        }

        if (!foundAny) {
            return null;
        }

        return { min, max };
    }

    /**
     * Whether a mesh should contribute to framing bounds (visible, enabled,
     * not an internal `__` helper — except built-in content and the anchor preview).
     */
    private static isFramableMesh(mesh: AbstractMesh): boolean {
        if (!mesh.isVisible || mesh.visibility <= 0 || !mesh.isEnabled()) return false;
        if (mesh.name.endsWith(COLLIDER_MESH_SUFFIX) || mesh.name.endsWith(SELECTION_MESH_SUFFIX)) return false;
        if (mesh.name === ANCHOR_PREVIEW_MESH_NAME) return true;
        // Exported built-ins retain these names when imported from GLZ. They
        // are visible scene content, not helpers; excluding them makes a
        // primitive-only model frame as the empty-node fallback after reload.
        if (mesh.name.startsWith('__builtin_')) return true;
        return !mesh.name.startsWith('__');
    }

    /**
     * Expand min/max in place by the mesh's world bounding box.
     * Skips meshes with no geometry and degenerate world bounds — some helper
     * meshes (notably BoxCollider's invisible picker mesh, visibility=0) never
     * have their bounding info refreshed by the render pipeline, so their world
     * bounding box stays at Babylon's "unset" sentinel
     * (min=+MAX_VALUE, max=-MAX_VALUE). Feeding those into the aggregate blows
     * it out to ±MAX_VALUE and breaks framing.
     *
     * @returns true if the mesh contributed to the bounds
     */
    private static expandByMeshBounds(mesh: AbstractMesh, min: Vector3, max: Vector3): boolean {
        const HUGE = 1e30;
        mesh.computeWorldMatrix(true);
        if (!mesh.subMeshes || mesh.subMeshes.length === 0 || mesh.infiniteDistance) return false;
        const bi = mesh.getBoundingInfo();
        const mn = bi.boundingBox.minimumWorld;
        const mx = bi.boundingBox.maximumWorld;
        // Reject sentinel/NaN/huge bounds. A mesh whose bounding info was
        // never refreshed (e.g. invisible picker mesh) has minimumWorld at
        // +MAX_VALUE and maximumWorld at -MAX_VALUE — both fail the > HUGE
        // check below, so we don't need a separate "inverted bounds" guard.
        if (!isFinite(mn.x + mn.y + mn.z + mx.x + mx.y + mx.z)) return false;
        if (Math.abs(mn.x) > HUGE || Math.abs(mn.y) > HUGE || Math.abs(mn.z) > HUGE) return false;
        if (Math.abs(mx.x) > HUGE || Math.abs(mx.y) > HUGE || Math.abs(mx.z) > HUGE) return false;
        // Content scaled to zero (a hidden build step) is a point at its group's
        // origin, wherever that is parked; it draws nothing, so it frames nothing.
        if (mn.equals(mx)) return false;

        if (mn.x < min.x) min.x = mn.x;
        if (mn.y < min.y) min.y = mn.y;
        if (mn.z < min.z) min.z = mn.z;
        if (mx.x > max.x) max.x = mx.x;
        if (mx.y > max.y) max.y = mx.y;
        if (mx.z > max.z) max.z = mx.z;
        return true;
    }

    /**
     * Get the world bounding box of a node and its descendants, with the same
     * degenerate-bounds defenses as scene framing. Unlike Babylon's
     * `getHierarchyBoundingVectors`, this never returns the ±MAX_VALUE
     * sentinel — it returns null when nothing renderable contributes
     * (empty transform, particle emitter, collider-only object).
     */
    public static getNodeBounds(node: TransformNode): { min: Vector3, max: Vector3 } | null {
        node.getScene().incrementRenderId();
        node.computeWorldMatrix(true);

        const meshes = node.getChildMeshes(false);
        if (node instanceof AbstractMesh) {
            meshes.push(node);
        }

        const min = new Vector3(Number.MAX_VALUE, Number.MAX_VALUE, Number.MAX_VALUE);
        const max = new Vector3(-Number.MAX_VALUE, -Number.MAX_VALUE, -Number.MAX_VALUE);
        let foundAny = false;
        for (const mesh of meshes) {
            if (!this.isFramableMesh(mesh)) continue;
            foundAny = this.expandByMeshBounds(mesh, min, max) || foundAny;
        }

        return foundAny && !min.equals(max) ? { min, max } : null;
    }

    /**
     * Get the scene bounding box (min/max vectors).
     * Walks eligible meshes ourselves rather than using Scene.getWorldExtends
     * so we can skip meshes with degenerate world bounds (see
     * {@link expandByMeshBounds}) — otherwise large scenes look tiny and the
     * camera ends up under-zoomed via the 3-unit-cube fallback.
     */
    private static getSceneBounds(scene: Scene): { min: Vector3, max: Vector3 } {
        let min = new Vector3(Number.MAX_VALUE, Number.MAX_VALUE, Number.MAX_VALUE);
        let max = new Vector3(-Number.MAX_VALUE, -Number.MAX_VALUE, -Number.MAX_VALUE);

        for (const mesh of scene.meshes) {
            if (!this.isFramableMesh(mesh)) continue;
            this.expandByMeshBounds(mesh, min, max);
        }

        // If nothing contributed, min/max stay at the +/-MAX_VALUE sentinel and
        // the particle/cube fallback below takes over.
        const isInvalidBounds =
            min.equals(new Vector3(Number.MAX_VALUE, Number.MAX_VALUE, Number.MAX_VALUE)) ||
            max.equals(new Vector3(-Number.MAX_VALUE, -Number.MAX_VALUE, -Number.MAX_VALUE)) ||
            min.equals(max);

        if (isInvalidBounds) {
            Logger.log('No valid mesh bounds found, attempting to estimate from particle systems');
            const particleBounds = this.estimateParticleSystemBounds(scene);

            if (particleBounds) {
                min = particleBounds.min;
                max = particleBounds.max;
                Logger.log(`Using particle system bounds: ${min.toString()} to ${max.toString()}`);
            } else {
                Logger.log('No particle systems found, using default 3-unit cube');
                min = new Vector3(-1.5, -1.5, -1.5);
                max = new Vector3(1.5, 1.5, 1.5);
            }
        }

        return { min, max };
    }

    /**
     * Set the camera's clip planes.
     *
     * The planes start at {@link CAMERA_MIN_Z} / {@link CAMERA_MAX_Z} rather
     * than being derived from the framed radius. A scene-scaled near plane
     * moves every time the scene is re-framed, and on a room-sized scene it
     * lands metres in front of the camera — so anything the user then moves
     * close to is sliced away. On the 24-bit depth buffer the fixed planes
     * resolve ~6 um at 1 m and ~0.6 mm at 10 m; past that the near plane
     * follows the orbit distance, see {@link SceneCamera.syncNearPlane}.
     *
     * The only scale dependence left is a guard at the two extremes, where the
     * fixed pair would clip the scene outright: a sub-decimetre scene lowers
     * the near plane, a scene hundreds of metres across raises the far plane.
     * An ordinary scene gets CAMERA_MAX_Z and, up to about 10 m of orbit
     * distance, exactly CAMERA_MIN_Z.
     *
     * @param camera - Camera to set the planes on
     * @param sceneRadius - Framed radius, used only for the extreme-scale guard
     */
    public static applyClipPlanes(camera: ArcRotateCamera, sceneRadius: number): void {
        nearFloors.set(camera, Math.max(Math.min(CAMERA_MIN_Z, sceneRadius * 0.05), 0.0001));
        camera.maxZ = Math.max(CAMERA_MAX_Z, sceneRadius * 10);
        this.syncNearPlane(camera);
    }

    /**
     * Raise the near plane as the camera moves away from its target.
     *
     * A 24-bit depth step at distance d is d^2 / (minZ * 2^24), so the fixed
     * 1 cm plane resolves 4 mm at 27 m, coarser than the 1.5 mm a text sits
     * above its panel. The plane follows the orbit distance instead, never
     * below the floor {@link applyClipPlanes} set and never beyond 0.5 m, so
     * nothing near the target is sliced away.
     */
    private static syncNearPlane(camera: ArcRotateCamera): void {
        const floor = nearFloors.get(camera) ?? CAMERA_MIN_Z;
        const d = camera.radius;
        const wanted = (d * d) / (CAMERA_MAX_DEPTH_STEP * 2 ** 24);
        const near = Math.max(floor, Math.min(wanted, CAMERA_MAX_NEAR_Z));
        // A projection rebuild per frame of a zoom is wasted work for a 2% move.
        if (Math.abs(near - camera.minZ) > camera.minZ * 0.02) camera.minZ = near;
    }

    public static calculateSceneBoundingBox(scene: Scene, camera: ArcRotateCamera, config: ConfigData, safeArea?: Readonly<SafeAreaInsets>): void {
        const { min, max } = this.getSceneBounds(scene);

        // Use the framing behavior to automatically position the camera
        const framingBehavior = camera.getBehaviorByName("Framing") as FramingBehavior;
        if (framingBehavior) {
            // Preserve the original camera angles
            const originalAlpha = camera.alpha;
            const originalBeta = camera.beta;

            // Apply the cameraRadius config as a radius scale
            if (config.cameraRadius !== 1) {
                framingBehavior.radiusScale = config.cameraRadius;
            }

            // Temporarily remove radius limits so zoomOnBoundingInfo can set
            // the ideal radius for scenes of any scale (the initial limits from
            // setupCamera are small and would clamp large scenes)
            camera.lowerRadiusLimit = 0;
            camera.upperRadiusLimit = Infinity;

            zoomInto(camera, framingBehavior, min, max, safeArea);

            // Restore the original camera angles (only adjust radius and target)
            camera.alpha = originalAlpha;
            camera.beta = originalBeta;

            // Adjust camera limits to support models of any scale
            // Allow zooming closer (5% of radius) and further (5x radius)
            camera.lowerRadiusLimit = camera.radius * 0.05;
            camera.upperRadiusLimit = camera.radius * 5;

            this.applyClipPlanes(camera, camera.radius);

            Logger.log(`Camera framed at radius ${camera.radius.toFixed(3)} for bounds ${max.subtract(min).length().toFixed(3)}`);
        }

        if (config.mode) {
            const size = max.subtract(min).length();

            const renderBounds = new TransformNode(RENDER_BOUNDS_NAME);
            const maxSphere = CreateSphere("maxSphere", { diameter: size * 0.05 }, scene);
            const minSphere = CreateSphere("minSphere", { diameter: size * 0.05 }, scene);
            maxSphere.parent = renderBounds;
            minSphere.parent = renderBounds;
            minSphere.position = min;
            maxSphere.position = max;
            renderBounds.setEnabled(false);
        }
    }

    /**
     * Reset camera to frame the scene, re-centering the view on the object.
     * Useful after panning has offset the camera target.
     * 
     * @param scene - The Babylon.js scene
     * @param camera - The ArcRotateCamera to reset
     * @param animate - Whether to animate the transition (default: true)
     */
    public static resetCamera(scene: Scene, camera: ArcRotateCamera, animate: boolean = true, safeArea?: Readonly<SafeAreaInsets>): void {
        const { min, max } = this.getSceneBounds(scene);
        const framingBehavior = camera.getBehaviorByName("Framing") as FramingBehavior;
        
        if (framingBehavior) {
            applyTemporaryFraming(framingBehavior, { framingTime: animate ? 500 : 0 }, animate ? 600 : 100);
            zoomInto(camera, framingBehavior, min, max, safeArea);
        } else {
            // Fallback: manually set camera target and radius
            const view = framingWindow(camera, safeArea);
            setLensShift(camera, view.x, view.y);
            const center = min.add(max).scale(0.5);
            camera.setTarget(center);
            const idealRadius = boxFramingRadius(camera, min, max, { positionScale: 0.5, radiusScale: 1.5 }, view)
                ?? max.subtract(min).length() * 1.5 / Math.min(view.width, view.height);

            camera.radius = idealRadius;
            
            Logger.log(`Camera reset to center: ${center.toString()}, radius: ${idealRadius.toFixed(3)}`);
        }
    }

    /**
     * Focus (frame) the camera on a single node and its descendants — the
     * editor "F" hotkey. Animates the camera target to the node's bounds
     * center and the radius to fit the bounds, then restores the framing
     * behavior's settings.
     *
     * Objects with no renderable bounds (empty transforms, particle emitters,
     * collider-only objects) are framed as a 1-unit box around their origin
     * so focus still centers them instead of flying off to the sentinel
     * bounds Babylon would report.
     *
     * @param camera - The ArcRotateCamera to move (must have a FramingBehavior)
     * @param node - Root transform of the object to frame
     * @param options - Padding and animation duration overrides
     * @returns true if framing was applied
     */
    public static focusOnNode(camera: ArcRotateCamera, node: TransformNode, options?: CameraFocusOptions, safeArea?: Readonly<SafeAreaInsets>): boolean {
        const framingBehavior = camera.getBehaviorByName("Framing") as FramingBehavior | null;
        if (!framingBehavior) {
            Logger.log('Cannot focus node - camera has no FramingBehavior');
            return false;
        }

        const radiusScale = options?.radiusScale ?? 1.5;
        const framingTime = options?.framingTime ?? 300;

        let bounds = this.getNodeBounds(node);
        if (!bounds) {
            const center = node.getAbsolutePosition();
            const half = new Vector3(0.5, 0.5, 0.5);
            bounds = { min: center.subtract(half), max: center.add(half) };
        }

        applyTemporaryFraming(framingBehavior, { framingTime, radiusScale }, framingTime + 100);
        zoomInto(camera, framingBehavior, bounds.min, bounds.max, safeArea);
        return true;
    }

    /**
     * Focus (frame) the camera on the combined bounds of several nodes — the
     * editor "F" hotkey while multiple objects are selected. Unions rendered
     * bounds and 1-unit boxes for empty roots. Bounds-less descendants of a
     * selected rendered root do not enlarge it, even when their origins lie
     * outside its drawn bounds; focusing an empty node alone still uses its origin.
     *
     * @param camera - The ArcRotateCamera to move (must have a FramingBehavior)
     * @param nodes - Root transforms of the objects to frame
     * @param options - Padding and animation duration overrides
     * @returns true if framing was applied
     */
    public static focusOnNodes(camera: ArcRotateCamera, nodes: TransformNode[], options?: CameraFocusOptions, safeArea?: Readonly<SafeAreaInsets>): boolean {
        if (nodes.length <= 1) {
            return nodes.length === 1 ? this.focusOnNode(camera, nodes[0], options, safeArea) : false;
        }

        const framingBehavior = camera.getBehaviorByName("Framing") as FramingBehavior | null;
        if (!framingBehavior) {
            Logger.log('Cannot focus nodes - camera has no FramingBehavior');
            return false;
        }

        const radiusScale = options?.radiusScale ?? 1.5;
        const framingTime = options?.framingTime ?? 300;

        const min = new Vector3(Number.MAX_VALUE, Number.MAX_VALUE, Number.MAX_VALUE);
        const max = new Vector3(-Number.MAX_VALUE, -Number.MAX_VALUE, -Number.MAX_VALUE);
        const measured = nodes.map(node => ({ node, bounds: this.getNodeBounds(node) }));
        const renderedRoots = measured.filter(({ bounds }) => bounds !== null).map(({ node }) => node);
        for (const { node, bounds: drawn } of measured) {
            let bounds = drawn;
            if (!bounds) {
                if (renderedRoots.some(root => node.isDescendantOf(root))) continue;
                const center = node.getAbsolutePosition();
                const half = new Vector3(0.5, 0.5, 0.5);
                bounds = { min: center.subtract(half), max: center.add(half) };
            }
            if (bounds.min.x < min.x) min.x = bounds.min.x;
            if (bounds.min.y < min.y) min.y = bounds.min.y;
            if (bounds.min.z < min.z) min.z = bounds.min.z;
            if (bounds.max.x > max.x) max.x = bounds.max.x;
            if (bounds.max.y > max.y) max.y = bounds.max.y;
            if (bounds.max.z > max.z) max.z = bounds.max.z;
        }

        applyTemporaryFraming(framingBehavior, { framingTime, radiusScale }, framingTime + 100);
        zoomInto(camera, framingBehavior, min, max, safeArea);
        return true;
    }

}
